import { useRef, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  saveDonation,
  updateDonation,
  uploadDonationReceipt,
  deleteDonationReceipt,
  type Donation,
} from '@/services/donations';

interface DonationModalProps {
  donation?: Donation | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function DonationModal({ donation, onClose, onSaved }: DonationModalProps) {
  const isEditing = !!donation;

  const [periodName, setPeriodName] = useState(
    donation?.periodName ?? donation?.beneficiaryName ?? '',
  );
  const [phone, setPhone] = useState(donation?.beneficiaryPhone ?? '');
  const [address, setAddress] = useState(donation?.beneficiaryAddress ?? '');
  const [donationRate, setDonationRate] = useState(
    donation?.donationRate != null ? String(donation.donationRate) : '2',
  );
  const [isDelivered, setIsDelivered] = useState(donation?.isDelivered ?? false);
  const [receiptUrl, setReceiptUrl] = useState(donation?.receiptDocumentUrl ?? '');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalReceiptRef = useRef<string>(donation?.receiptDocumentUrl ?? '');
  const uploadedRef = useRef<string[]>([]);
  const savedRef = useRef(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');

    const { url, error: upErr } = await uploadDonationReceipt(file);
    setUploading(false);

    if (upErr) {
      setError(`Error al subir "${file.name}": ${upErr}`);
      return;
    }
    if (url) {
      uploadedRef.current.push(url);
      setReceiptUrl(url);
      setFileName(file.name);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeFile() {
    setReceiptUrl('');
    setFileName('');
  }

  async function handleClose() {
    if (!savedRef.current) {
      for (const url of uploadedRef.current) {
        await deleteDonationReceipt(url);
      }
    }
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!periodName.trim()) {
      setError('El nombre de la fundación es obligatorio.');
      return;
    }

    const beneficiaryName = periodName.trim();
    setSaving(true);
    let saveError: string | null;
    if (isEditing && donation) {
      ({ error: saveError } = await updateDonation({
        id: donation.id,
        periodName: beneficiaryName,
        beneficiaryName,
        beneficiaryPhone: phone,
        beneficiaryAddress: address,
        salesBaseAmount: 0,
        donationRate: Number(donationRate) || 0,
        receiptUrl,
        isDelivered,
        originalDeliveredAt: donation.deliveredAt,
      }));
    } else {
      ({ error: saveError } = await saveDonation({
        periodName: beneficiaryName,
        beneficiaryName,
        beneficiaryPhone: phone,
        beneficiaryAddress: address,
        salesBaseAmount: 0,
        donationRate: Number(donationRate) || 0,
      }));
    }
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }

    if (isEditing) {
      const finalUrl = receiptUrl.trim();
      if (originalReceiptRef.current && originalReceiptRef.current !== finalUrl) {
        await deleteDonationReceipt(originalReceiptRef.current);
      }
      for (const url of uploadedRef.current) {
        if (url !== finalUrl) {
          await deleteDonationReceipt(url);
        }
      }
    }

    savedRef.current = true;
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Donación' : 'Nueva Donación'}
      subtitle={
        isEditing
          ? `Editando: ${donation?.periodName || donation?.beneficiaryName}`
          : 'Registra la fundación u organización beneficiaria.'
      }
      onClose={handleClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Fundación / Organización *</label>
          <input
            className={inputCls}
            value={periodName}
            onChange={(e) => setPeriodName(e.target.value)}
            placeholder="Fundación Patitas con Propósito"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Teléfono</label>
            <input
              className={inputCls}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+57 3xx xxx xxxx"
            />
          </div>
          <div>
            <label className={labelCls}>Dirección</label>
            <input
              className={inputCls}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Cra 45 # 12-34, Medellín"
            />
          </div>
        </div>
        <div>
          <label className={labelCls}>Porcentaje donación (%) — informativo</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            className={inputCls}
            value={donationRate}
            onChange={(e) => setDonationRate(e.target.value)}
          />
          <p className="text-xs text-foreground-400 mt-1.5">
            Solo informativo: el porcentaje real se configura en la sección de Configuración.
          </p>
        </div>

        {isEditing && (
          <>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isDelivered}
                onChange={(e) => setIsDelivered(e.target.checked)}
                className="w-4 h-4 accent-accent-500 cursor-pointer"
              />
              <span className="text-sm text-foreground-700">
                {isDelivered ? 'Donación entregada' : 'Marcar como entregada'}
              </span>
            </label>

            <div>
              <label className={labelCls}>Recibo / Comprobante</label>

              {fileName ? (
                <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
                  <i className="ri-file-text-line text-accent-600 text-lg"></i>
                  <span className="flex-1 text-sm text-foreground-800 truncate">{fileName}</span>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 hover:text-red-600 transition-colors cursor-pointer"
                    aria-label="Quitar comprobante"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFile}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 text-sm px-4 py-2.5 border border-dashed border-background-300 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>Subiendo...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-2-line"></i>Subir comprobante
                      </>
                    )}
                  </button>
                  <p className="text-xs text-foreground-400 mt-1.5">
                    Opcional. Se sube a Storage y se guarda la URL pública. También puedes pegar una URL abajo.
                  </p>
                </>
              )}
            </div>

            <div>
              <label className={labelCls}>URL soporte</label>
              <input
                className={inputCls}
                value={receiptUrl}
                onChange={(e) => {
                  setReceiptUrl(e.target.value);
                  setFileName('');
                }}
                placeholder="https://..."
              />
            </div>
          </>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={handleClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear donación'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}